import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // 🆕 Feb 2026 — Timeout 120s per evitare hang infiniti su estrazioni PDF pesanti.
  // Le richieste oltre questo limite falliscono con `ECONNABORTED` gestito da formatApiError.
  timeout: 120000,
});

// Attach bearer token from localStorage as fallback (cookie + bearer dual mode)
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("access_token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  // Token OTP "sensitive" per modifica impostazioni protette (Email SMTP, Area Pubblica)
  const sotp = sessionStorage.getItem("sensitive_otp_token");
  if (sotp) cfg.headers["X-Sensitive-Otp-Token"] = sotp;
  // 🆕 Feb 2026 — Portale Cliente (vista staff): riscrive `/my/*` in
  // `/admin/clients/{cid}/portal-view/*` quando l'admin sta guardando l'area del cliente.
  const viewCid = sessionStorage.getItem("portal_view_client_id");
  if (viewCid && cfg.url) {
    const raw = cfg.url;
    // Solo GET: le POST/PATCH/DELETE non vengono mai riscritte (nessuna scrittura in preview)
    const method = String(cfg.method || "get").toLowerCase();
    if (method === "get" && (raw === "/my/portfolio" || raw.startsWith("/my/portfolio?"))) {
      cfg.url = raw.replace("/my/portfolio", `/admin/clients/${viewCid}/portal-view/portfolio`);
    } else if (method === "get" && (raw === "/my/quotes" || raw.startsWith("/my/quotes?"))) {
      cfg.url = raw.replace("/my/quotes", `/admin/clients/${viewCid}/portal-view/quotes`);
    } else if (method === "get" && (raw === "/my/quote-offers" || raw.startsWith("/my/quote-offers?"))) {
      cfg.url = raw.replace("/my/quote-offers", `/admin/clients/${viewCid}/portal-view/quote-offers`);
    } else if (method === "get") {
      // /my/quotes/{qid}/messages → /admin/clients/{cid}/portal-view/quotes/{qid}/messages
      const m = raw.match(/^\/my\/quotes\/([^/]+)\/messages(\?.*)?$/);
      if (m) cfg.url = `/admin/clients/${viewCid}/portal-view/quotes/${m[1]}/messages${m[2] || ""}`;
    }
    // 🆕 Fix Feb 2026 — Disabilita CACHE per la vista admin dell'area cliente.
    // Ogni apertura deve fare fetch fresca dal backend:
    //   1) Header no-cache che informa browser/CDN di non servire copie cached
    //   2) Cache-buster `_ts` per invalidare eventuali intermediari (Cloudflare, proxy)
    if (method === "get" && cfg.url && cfg.url.includes("/portal-view/")) {
      cfg.headers = cfg.headers || {};
      cfg.headers["Cache-Control"] = "no-cache, no-store, must-revalidate";
      cfg.headers["Pragma"] = "no-cache";
      cfg.headers["Expires"] = "0";
      const sep = cfg.url.includes("?") ? "&" : "?";
      cfg.url = `${cfg.url}${sep}_ts=${Date.now()}`;
    }
  }
  return cfg;
});

// Quando il backend risponde 401 OTP_EXPIRED, ripuliamo il token così la UI può richiederne uno nuovo.
api.interceptors.response.use(
  (res) => res,
  (err) => {
    const detail = err?.response?.data?.detail;
    if (err?.response?.status === 401 && detail === "OTP_EXPIRED") {
      sessionStorage.removeItem("sensitive_otp_token");
    }
    return Promise.reject(err);
  }
);

export const formatApiError = (err) => {
  // 🆕 Feb 2026 — Messaggio chiaro sul timeout invece dell'ECONNABORTED axios cifrato
  if (err?.code === "ECONNABORTED" || /timeout/i.test(err?.message || "")) {
    return "Timeout: il server sta impiegando troppo tempo per rispondere. Riprova o carica un file più piccolo.";
  }
  const d = err?.response?.data?.detail;
  if (!d) return err.message || "Errore di rete";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    // Pydantic validation errors: {loc:["body","field"], msg:"...", type:"..."}
    // Traduci i tipi comuni + estrai il nome del campo
    const FIELD_LABELS = {
      // Polizze
      client_id: "Cliente",
      company_id: "Compagnia",
      broker_id: "Broker / Agenzia",
      type: "Tipo polizza",
      policy_number: "Numero polizza",
      start_date: "Data inizio",
      end_date: "Data scadenza",
      premium: "Premio",
      commission_percent: "% Provvigione",
      commission_amount: "Provvigione (€)",
      frazionamento: "Frazionamento",
      vehicle_plate: "Targa veicolo",
      vehicle_type: "Tipo veicolo",
      vehicle_make: "Marca veicolo",
      vehicle_model: "Modello veicolo",
      status: "Stato",
      taxable_base: "Imponibile",
      net_premium: "Premio netto",
      paid_amount: "Cifra incassata",
      policy_cost: "Costo polizza",
      extra_fees_total: "FEE aggiuntiva (Agen.)",
      commission_cost: "Fee inserite dal Broker/Ag.",
      suspension_start_date: "Data sospensione",
      reactivation_date: "Data riattivazione",
      // Clienti
      first_name: "Nome",
      last_name: "Cognome",
      name: "Nome / Ragione sociale",
      fiscal_code: "Codice fiscale",
      vat_number: "Partita IVA",
      email: "Email",
      phone: "Telefono",
      mobile: "Cellulare",
      birth_date: "Data di nascita",
      birth_place: "Luogo di nascita",
      address: "Indirizzo",
      city: "Città",
      postal_code: "CAP",
      province: "Provincia",
      country: "Nazione",
      client_kind: "Tipo cliente",
      profession: "Professione",
      marital_status: "Stato civile",
      gender: "Sesso",
      // Compagnie
      company_name: "Nome compagnia",
      company_code: "Codice compagnia",
      // Broker / Agenzia
      contact_person: "Referente",
      pec: "PEC",
      rui: "Iscrizione RUI",
      agency_share_percent: "% quota agenzia",
      broker_commission_share_percent: "% retrocessione broker",
    };
    const MSG_MAP = {
      missing: "campo obbligatorio",
      "value_error.missing": "campo obbligatorio",
      "type_error.none.not_allowed": "campo obbligatorio",
      "value_error.number.not_finite": "valore numerico non valido",
    };
    const parts = d.map((e) => {
      const loc = Array.isArray(e?.loc) ? e.loc.filter((s) => s !== "body") : [];
      const fieldKey = loc[loc.length - 1];
      const label = FIELD_LABELS[fieldKey] || fieldKey || "(campo)";
      const msgRaw = e?.msg || e?.type || "";
      const friendly = MSG_MAP[e?.type] || MSG_MAP[msgRaw] || msgRaw || "valore non valido";
      return `${label}: ${friendly}`;
    });
    return parts.join(" · ");
  }
  return String(d);
};

// Formatter italiano deterministico: . per migliaia, , per decimali (indipendente da locale del runtime)
const _formatItNumber = (n, decimals = 2) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return decimals > 0 ? "0," + "0".repeat(decimals) : "0";
  const fixed = num.toFixed(decimals);
  const negative = fixed.startsWith("-");
  const [intRaw, dec] = (negative ? fixed.slice(1) : fixed).split(".");
  const intWithSep = intRaw.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const out = decimals > 0 ? `${intWithSep},${dec}` : intWithSep;
  return negative ? `-${out}` : out;
};

export const formatCurrency = (n) => `€ ${_formatItNumber(n, 2)}`;

export const formatAmount = (n, decimals = 2) => _formatItNumber(n, decimals);

// 🆕 Feb 2026 — Formattazione dimensione file (auto KB/MB/GB con 1 decimale).
// Ritorna es. "512 B", "48.3 KB", "1.2 MB", "3.7 GB".
export const formatFileSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

// Validazione targhe italiane:
// - Auto nuova:            AA123BB       (^[A-Z]{2}\d{3}[A-Z]{2}$)
// - Auto vecchia prov:     PA121234      (^[A-Z]{2}\d{6}$)
// - Ciclomotore:           X0A1B2        (^[A-Z0-9]{6}$, 6 caratteri alfanumerici)
// - Rimorchi/moto:         AB12345       (^[A-Z]{2}\d{5}$)
// - Quadriciclo leggero:   XB95TC        (^[A-Z]{2}\d{2}[A-Z]{2}$)
export const PLATE_PATTERNS = {
  auto_nuova: /^[A-Z]{2}\d{3}[A-Z]{2}$/,
  auto_vecchia: /^[A-Z]{2}\d{6}$/,
  moto_rimorchio: /^[A-Z]{2}\d{5}$/,
  quadriciclo_leggero: /^[A-Z]{2}\d{2}[A-Z]{2}$/,
  ciclomotore: /^[A-Z0-9]{6}$/,
};

export const normalizePlate = (s) => String(s || "").toUpperCase().replace(/\s+/g, "").replace(/[^A-Z0-9]/g, "");

export const validatePlate = (raw) => {
  const p = normalizePlate(raw);
  if (!p) return { ok: false, type: null, message: "Targa obbligatoria" };
  for (const [key, re] of Object.entries(PLATE_PATTERNS)) {
    if (re.test(p)) return { ok: true, type: key, value: p, message: null };
  }
  return {
    ok: false,
    type: null,
    value: p,
    message: "Formato non valido. Usa AA123BB (nuova) · PA121234 (vecchia) · X0A1B2 (ciclomotore) · XB95TC (quadriciclo leggero)",
  };
};

// Mobile italiano: 9-10 cifre che iniziano con 3 (es. 3331234567). Accetta anche prefisso +39 o 0039.
export const normalizeMobile = (s) =>
  String(s || "").replace(/[\s().-]/g, "").replace(/^(\+39|0039)/, "");

export const validateItalianMobile = (raw) => {
  const m = normalizeMobile(raw);
  if (!m) return { ok: false, value: "", message: "Cellulare obbligatorio" };
  if (!/^\d+$/.test(m)) return { ok: false, value: m, message: "Solo cifre, +39 o spazi" };
  if (m.length < 9 || m.length > 10) return { ok: false, value: m, message: "Deve avere 9-10 cifre" };
  if (!m.startsWith("3")) return { ok: false, value: m, message: "I cellulari italiani iniziano con 3" };
  return { ok: true, value: m, message: null };
};

// 🆕 28/07/2026 — Restituisce il numero in formato internazionale pronto per wa.me
// (solo cifre, con prefisso paese, senza "+" né spazi). Es. "329 1234567" → "393291234567".
// Ritorna stringa vuota se il numero è vuoto/non normalizzabile.
export const normalizeMobileIntl = (raw) => {
  if (!raw) return "";
  const n = String(raw).replace(/[^0-9+]/g, "");
  if (!n) return "";
  if (n.startsWith("00")) return n.slice(2);
  if (n.startsWith("+")) return n.slice(1);
  // 🆕 Riconoscimento formato internazionale IT già presente: "39" seguito da un cellulare
  // (che inizia con "3") o da un fisso (che inizia con "0"), lunghezza totale 12-13 cifre.
  if (n.length >= 12 && n.startsWith("39") && (n[2] === "3" || n[2] === "0")) return n;
  return "39" + n; // Numero locale italiano → aggiungi prefisso Italia
};

// 🆕 30/07/2026 — Costruisce un link WhatsApp usando lo schema `whatsapp://send?phone=…`
// (invece di wa.me) così su desktop apre direttamente l'app WhatsApp installata.
// Ritorna stringa vuota se il numero è vuoto/non valido.
export const buildWhatsappUrl = (mobile, text) => {
  const n = normalizeMobileIntl(mobile);
  if (!n) return "";
  const t = (text || "").trim();
  return t ? `whatsapp://send?phone=${n}&text=${encodeURIComponent(t)}` : `whatsapp://send?phone=${n}`;
};
// NB: Rimosso lookupMobileOperator: con la Mobile Number Portability (MNP) attiva dal 2002,
// il prefisso non identifica più l'operatore reale. Es: 328xxx assegnato originariamente a WindTre
// ma può essere TIM/Vodafone/Iliad se l'utente ha portato il numero. Per il vero gestore corrente
// servirebbe una HLR Lookup API a pagamento (es. NumVerify, Twilio).


export const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) {
      // Magari è una stringa "YYYY-MM-DD" o "DD/MM/YYYY" semplice
      const s = String(iso).trim();
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (m) return `${m[3]}/${m[2]}/${m[1]}`;
      const m2 = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
      if (m2) return s;
      return s;
    }
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return iso;
  }
};

export const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
  } catch {
    return iso;
  }
};

// 🆕 Feb 2026 — Formato data+ora al secondo (DD/MM/AAAA HH:MM:SS) per log/offerte partner
export const formatDateTimeSeconds = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso);
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    const ss = String(d.getSeconds()).padStart(2, "0");
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
  } catch {
    return iso;
  }
};
